import { withDependencies, named, multi, optional } from '@wix/thunderbolt-ioc'
import {
	BrowserWindowSymbol,
	ILogger,
	LoggerSymbol,
	PageFeatureConfigSymbol,
	pageIdSym,
	SiteFeatureConfigSymbol,
	TpaHandlerExtras,
	TpaHandlerProvider,
	TpaHandlerProviderSymbol,
	TpaHandlers,
	TpaIFrame,
} from '@wix/thunderbolt-symbols'
import { BaseResponse, TpaPageConfig } from './types'
import { name as tpaFeatureName } from './symbols'
import { ITpaHandlersManager, name as tpaCommonsName, TpaCommonsSiteConfig } from 'feature-tpa-commons'
import { runtimeTpaCompIdBuilder } from '@wix/thunderbolt-commons'
import { TbDebugSymbol, DebugApis } from 'feature-debug'
import { TpaWorkerSymbol, ITpaWorker } from 'feature-tpa-worker'
import { TPA_HANDLER_EMPTY_RESPONSE } from './utils/constants'

export const TpaHandlersManager = withDependencies(
	[
		named(SiteFeatureConfigSymbol, tpaCommonsName),
		named(PageFeatureConfigSymbol, tpaFeatureName),
		multi(TpaHandlerProviderSymbol),
		BrowserWindowSymbol,
		LoggerSymbol,
		pageIdSym,
		optional(TpaWorkerSymbol),
		optional(TbDebugSymbol),
	],
	(
		{ appsClientSpecMapData }: TpaCommonsSiteConfig,
		tpaPageConfig: TpaPageConfig,
		handlerProviders: Array<TpaHandlerProvider>,
		window: Window,
		logger: ILogger,
		pageId: string,
		tpaWorker?: ITpaWorker,
		debugApi?: DebugApis
	): ITpaHandlersManager => {
		const sendResponseTPA = ({
			tpa,
			callId,
			status,
			res,
			compId,
		}: {
			tpa: TpaIFrame
			callId: string
			status: boolean
			res: any
			compId: string
		}) => {
			const msg: BaseResponse<any> = {
				callId,
				intent: 'TPA_RESPONSE',
				status,
				res,
			}
			if (debugApi) {
				debugApi.tpa.addMessage({ msg, compId, contextId: pageId })
			}
			tpa.postMessage(JSON.stringify(msg), '*')
		}

		const handlers: TpaHandlers = Object.assign(
			{},
			...handlerProviders.map((provider) => provider.getTpaHandlers())
		)

		const getTpaWorkerAppDefinitionId = (compId: string) => {
			if (tpaWorker?.isTpaWorker(compId)) {
				return tpaWorker!.getAppDefinitionId(compId)
			}
			return null
		}

		return {
			async handleMessage(tpa, { type, callId, compId, data }) {
				const handler = handlers[type]
				const originCompId = runtimeTpaCompIdBuilder.getOriginCompId(compId)
				const tpaCompData = tpaPageConfig.widgets[originCompId]
				const appDefinitionId = tpaCompData?.appDefinitionId || getTpaWorkerAppDefinitionId(originCompId) || ''
				const widgetId = tpaCompData?.widgetId

				if (!handler) {
					console.warn(`TpaHandlerError: ${type} handler is not implemented`)
					logger.captureError(new Error('TPA handler is not implemented'), {
						tags: { feature: 'tpa', handlerName: type, appDefinitionId },
						extra: {
							handlerName: type,
							compId,
							originCompId,
							appDefinitionId,
							widgetId,
						},
					})
					return
				}

				const extras: TpaHandlerExtras = {
					callId,
					tpa,
					appDefinitionId,
					tpaCompData,
					appClientSpecMapData: appsClientSpecMapData[appDefinitionId],
					originCompId,
				}

				const result = handler(compId, data, extras)
				if (typeof result === 'undefined') {
					// TODO rethink this. it's a very weird way of saying:
					// if the handler returns a promise or some defined value,
					// someone in the iframe is waiting for a response. otherwise return.
					return
				}

				try {
					const res = await result
					if (res === TPA_HANDLER_EMPTY_RESPONSE) {
						// for tpa handlers with no onSuccess callback but only onFailure callback,
						// we want to send a response (i.e trigger the callback) only if the handler rejected.
						return
					}
					sendResponseTPA({ tpa, callId, compId, status: true, res })
				} catch (e) {
					const { message, name, stack } = e
					sendResponseTPA({
						tpa,
						callId,
						compId,
						status: false,
						res: { error: { message, name, stack } },
					})
				}
			},
		}
	}
)
